// Sopwith 3D - object views.
//
// One view per live simulation object: a model, its previous and current
// poses, and per-frame presentation (interpolation, prop spin, flapping,
// debris tumbling). Models come from models.ts; particle bursts are raised
// when explosions and bombs first appear.

import * as THREE from 'three';
import { SYM_HGHT, SYM_WDTH } from '../sim/constants';
import { Obj, ObjState, ObjType } from '../sim/types';
import { DUST, FIRE, type Particles, SPARK } from './effects';
import { lerpPose, poseOf, type Pose } from './interpolate';
import { addOutlines } from './materials';
import {
  buildBalloon,
  buildBird,
  buildBomb,
  buildDebris,
  buildFlock,
  buildMissile,
  buildOx,
  buildPlane,
  buildRubble,
  buildShot,
  buildSmoke,
  buildStarburst,
  buildTarget,
  SHARED,
} from './models';

export { factionColors } from './models';

const NO_OUTLINE = new Set([
  ObjType.SHOT,
  ObjType.SMOKE,
  ObjType.EXPLOSION,
  ObjType.STARBURST,
]);

/** A view tracks one Obj: its mesh, its previous and current poses. */
export class ObjView {
  readonly prev: Pose = { x: 0, y: 0, heading: 0, orient: 0 };
  readonly cur: Pose = { x: 0, y: 0, heading: 0, orient: 0 };
  readonly tmp: Pose = { x: 0, y: 0, heading: 0, orient: 0 };
  root: THREE.Object3D;
  stamp = 0;
  private wings: THREE.Object3D[] = [];
  private prop: THREE.Object3D | null = null;
  private propDisc: THREE.Object3D | null = null;
  private key = '';
  private age = 0;
  private spin = Math.random() * 6;

  constructor(
    readonly ob: Obj,
    readonly parent: THREE.Object3D,
    particles: Particles | null,
  ) {
    this.root = new THREE.Group();
    parent.add(this.root);
    poseOf(ob, this.cur);
    Object.assign(this.prev, this.cur);
    this.rebuild();
    if (particles) {
      this.spawnEffect(particles);
    }
  }

  private spawnEffect(p: Particles): void {
    const ob = this.ob;
    const cx = ob.x + 8;
    const cy = ob.y - 8;
    if (ob.type === ObjType.EXPLOSION) {
      // Each debris piece adds a few sparks; the first ones add the fireball.
      p.emit(cx, cy, 0, 4, 40, FIRE, 0.5, 6);
      p.emit(cx, cy, 0, 2, 70, SPARK, 0.35, 3);
    } else if (ob.type === ObjType.SMOKE) {
      p.emit(cx, cy, 0, 1, 6, DUST, 0.8, 5);
    }
  }

  private meshKey(): string {
    const ob = this.ob;
    switch (ob.type) {
      case ObjType.PLANE:
        return `plane:${ob.clr}:${ob.state === ObjState.CRASHED ? 'x' : ''}`;
      case ObjType.TARGET:
        return `target:${ob.orient}:${ob.clr}:${ob.state === ObjState.STANDING ? 'up' : 'down'}`;
      case ObjType.OX:
        return `ox:${ob.state}`;
      case ObjType.BALLOON:
        return `balloon:${ob.clr}`;
      default:
        return `${ob.type}`;
    }
  }

  rebuild(): void {
    const key = this.meshKey();
    if (key === this.key) return;
    this.key = key;
    this.root.clear();
    const ob = this.ob;
    const w = ob.symbol?.w ?? SYM_WDTH;
    const h = ob.symbol?.h ?? SYM_HGHT;
    let mesh: THREE.Object3D;
    switch (ob.type) {
      case ObjType.PLANE:
        mesh = buildPlane(ob.clr);
        if (ob.state === ObjState.CRASHED) {
          mesh.rotation.z = -0.55;
          mesh.position.y = -3;
        }
        break;
      case ObjType.TARGET:
        mesh =
          ob.state === ObjState.STANDING ? buildTarget(ob.orient, ob.clr, w, h) : buildRubble(w, h);
        break;
      case ObjType.OX:
        mesh = buildOx();
        if (ob.state !== ObjState.STANDING) {
          mesh.rotation.x = Math.PI / 2; // on its side
          mesh.position.y = -4;
        }
        break;
      case ObjType.BIRD:
        mesh = buildBird(1.1);
        break;
      case ObjType.FLOCK:
        mesh = buildFlock();
        break;
      case ObjType.BOMB:
        mesh = buildBomb();
        break;
      case ObjType.MISSILE:
        mesh = buildMissile();
        break;
      case ObjType.SHOT:
        mesh = buildShot();
        break;
      case ObjType.BALLOON:
        mesh = buildBalloon(ob.clr);
        break;
      case ObjType.EXPLOSION:
        mesh = buildDebris();
        break;
      case ObjType.STARBURST:
        mesh = buildStarburst();
        break;
      case ObjType.SMOKE:
        mesh = buildSmoke();
        break;
      default:
        mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, 4), SHARED.dark);
        break;
    }
    if (!NO_OUTLINE.has(ob.type)) {
      addOutlines(mesh, ob.type === ObjType.BIRD || ob.type === ObjType.FLOCK ? 0.35 : 0.7);
    }
    this.root.add(mesh);
    this.wings = [];
    mesh.traverse((o) => {
      if (o.name === 'wing') this.wings.push(o);
    });
    this.prop = mesh.getObjectByName('prop') ?? null;
    this.propDisc = mesh.getObjectByName('propdisc') ?? null;
  }

  /** Called once per simulation tick: shift current to previous, read new. */
  commit(): void {
    Object.assign(this.prev, this.cur);
    poseOf(this.ob, this.cur);
    this.rebuild();
    this.age++;
  }

  /** Position the mesh for a frame `t` (0..1) of the way through the tick. */
  present(t: number, time: number): void {
    const ob = this.ob;
    const sym = ob.symbol;
    const w = sym?.w ?? SYM_WDTH;
    const h = sym?.h ?? SYM_HGHT;
    const p = lerpPose(this.prev, this.cur, t, this.tmp);
    this.root.position.set(p.x + w / 2, p.y - h / 2, 0);
    this.root.visible = ob.drwflg && !!sym;

    switch (ob.type) {
      case ObjType.PLANE: {
        const falling = ob.state === ObjState.FALLING;
        // Roll about the plane's own axis first, then apply the heading, so a
        // rolled (left-facing) plane still pitches the way the sim says.
        this.root.rotation.set(
          p.orient ? Math.PI : 0,
          falling ? time * 3 : 0,
          p.heading * Math.PI * 2,
          'ZYX',
        );
        const running = ob.speed > 0 && ob.state !== ObjState.CRASHED;
        if (this.prop) this.prop.rotation.x = time * (running ? 45 : 2);
        if (this.propDisc) this.propDisc.visible = running;
        break;
      }
      case ObjType.BOMB:
      case ObjType.MISSILE:
        this.root.rotation.z = Math.atan2(ob.dy, ob.dx || 0.001);
        break;
      case ObjType.EXPLOSION: {
        this.root.rotation.set(time * 7 + this.spin, time * 5, this.spin);
        // Glow fades to ember as the piece ages.
        const m = (this.root.children[0] as THREE.Mesh).material as THREE.MeshBasicMaterial;
        if (m === SHARED.fire && this.age > 6) {
          (this.root.children[0] as THREE.Mesh).material = SHARED.ember;
        }
        break;
      }
      case ObjType.SMOKE: {
        const s = 1 + this.age * 0.35;
        this.root.scale.setScalar(s);
        this.root.position.y += this.age * 0.4;
        break;
      }
      case ObjType.BIRD:
      case ObjType.FLOCK: {
        this.root.rotation.y = ob.dx < 0 ? Math.PI : 0;
        this.root.rotation.z =
          ob.type === ObjType.BIRD ? Math.atan2(ob.dy, Math.abs(ob.dx) || 1) : 0;
        this.root.position.y += Math.sin(time * 6) * 0.8;
        for (const wing of this.wings) {
          const phase = (wing.parent?.userData.phase as number | undefined) ?? 0;
          wing.rotation.x = (wing.userData.side as number) * Math.sin(time * 16 + phase) * 0.7;
        }
        break;
      }
      case ObjType.BALLOON:
        this.root.rotation.z = -ob.dx * 0.15;
        break;
      default:
        break;
    }
  }

  dispose(): void {
    this.parent.remove(this.root);
  }
}

/** Keeps a view for every live object, creating and removing as needed. */
export class ViewSet {
  private readonly views = new Map<Obj, ObjView>();
  private stamp = 0;
  private primed = false;

  constructor(
    private readonly parent: THREE.Object3D,
    private readonly particles: Particles | null = null,
  ) {}

  /** Sync with the object list after a tick. */
  commit(first: Obj | null): void {
    this.stamp++;
    for (let ob = first; ob; ob = ob.next) {
      let v = this.views.get(ob);
      if (!v) {
        // No effects for objects that exist when the view set is first filled.
        v = new ObjView(ob, this.parent, this.primed ? this.particles : null);
        this.views.set(ob, v);
      } else {
        v.commit();
      }
      v.stamp = this.stamp;
    }
    for (const [ob, v] of this.views) {
      if (v.stamp !== this.stamp) {
        v.dispose();
        this.views.delete(ob);
      }
    }
    this.primed = true;
  }

  present(t: number, time: number): void {
    for (const v of this.views.values()) {
      v.present(t, time);
    }
  }

  get(ob: Obj): ObjView | undefined {
    return this.views.get(ob);
  }

  get size(): number {
    return this.views.size;
  }

  clear(): void {
    for (const v of this.views.values()) v.dispose();
    this.views.clear();
    this.primed = false;
  }
}
